import './common/css/fonts.css';
import './common/css/theme.css';
import './App.css';

import { IconButton } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import type { Theme } from '@material-ui/core/styles';
import {
  createStyles,
  makeStyles,
  ThemeProvider,
} from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';
import { AuthState } from '@mogul/shared';
import clsx from 'clsx';
import { useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useAccount, useNetwork } from 'wagmi';

import { useAuth } from './auth';
import { BINANCE_PROMPT, BINANCE_PROMPT_INVALID } from './constants/prompt';
import { Login } from './containers/login/Login.container';
import { AuctionPage } from './containers/pages/auction/AuctionPage.container';
import { CollectionPage } from './containers/pages/collection/CollectionPage.container';
import { CommunityDashboardPage } from './containers/pages/community/CommunityDashboardPage.container';
import { CreatorsDashboard } from './containers/pages/creators-dashboard/CreatorsDashboard.container';
import { DashboardPage } from './containers/pages/dashboard/DashboardPage.container';
import { EventsPage } from './containers/pages/events/EventsPage.container';
import { FarmingPage } from './containers/pages/farming/FarmingPage.container';
import { LaunchPad } from './containers/pages/launchpad/LaunchPad.container';
import { LaunchPadListPage } from './containers/pages/launchpad-list/LaunchPadListPage.container';
import { MarketplacePage } from './containers/pages/marketplace/MarketplacePage.container';
import { OwnedNftMarketplacePage } from './containers/pages/marketplace/OwnedNftMarketplacePage.container';
import { Mogie } from './containers/pages/mogies/Mogie.container';
import { MogulLivePage } from './containers/pages/mogul-live/MogulLivePage.container';
import { MoonPay } from './containers/pages/moonpay/MoonPayPage.container';
import { NFTPage } from './containers/pages/nft/NFTPage.container';
import { NftDetailsPage } from './containers/pages/nft-details/NftDetailsPage.container';
import { NftMintingContainer } from './containers/pages/nft-minting/NftMinting.container';
import { ProfilePage } from './containers/pages/profile/ProfilePage.container';
import { FinancedProjectPage } from './containers/pages/project/FinancedProjectPage.container';
import { ProjectPage } from './containers/pages/project/ProjectPage.container';
import { ProjectSubmissionPage } from './containers/pages/project-submission/ProjectSubmissionPage.container';
import { RegisterPage } from './containers/pages/register/RegisterPage.container';
import { StarsStakingPage } from './containers/pages/starsStaking/StarsStakingPage.container';
import { UpdatesPage } from './containers/pages/updates/UpdatesPage.container';
import { PreviousRoundPage } from './containers/pages/voting/PreviousRoundPage.container';
import { VotingPage } from './containers/pages/voting/VotingPage.container';
import { ResetPasswordPage } from './containers/reset-password/ResetPasswordPage.container';
import { Sidebar } from './containers/sidebar/Sidebar';
import { TwoFactorAuth } from './containers/two-factor-auth/TwoFactorAuth.container';
import { WrongNetworkPage } from './containers/wrong-network/WrongNetwork.container';
import { useWeb3State } from './hooks';
import { CHAIN_ID_TO_TYPE_MAP, WEB3_CONFIG } from './lib';
import { createTheme } from './theme/theme';

const theme = createTheme({ type: 'light' });

const NOTIFICATION_MSG =
  'Please note BSC network is congested and transactions may take a longer period of time to succeed';

const useStyles = makeStyles<Theme, { isMoggiePage?: boolean }>(() =>
  createStyles({
    root: ({ isMoggiePage }) =>
      isMoggiePage
        ? {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }
        : {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            position: 'absolute',
            width: '100%',
          },
    exitButton: {
      width: theme.spacing(5),
      height: theme.spacing(5),
      backgroundColor: 'transparent',
    },
    exitIcon: {
      '&:hover': {
        width: theme.spacing(3),
        height: theme.spacing(3),
      },
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
      color: 'black',
    },
  }),
);

export const App = () => {
  const { isConnected } = useAccount();
  const { chain } = useNetwork();
  const { state, authenticated } = useAuth();
  const { walletDetails } = useWeb3State();
  const location = useLocation();

  const classes = useStyles({
    isMoggiePage: !location.pathname.includes('/platform/mogie'),
  });

  const [showBanner, setBanner] = useState(
    // !localStorage.getItem(BINANCE_PROMPT),
    false,
  );
  const inValidateBanner = () => {
    localStorage.setItem(BINANCE_PROMPT, BINANCE_PROMPT_INVALID);
    setBanner(false);
  };

  const isBsc = walletDetails.chain.id === WEB3_CONFIG.chains.bsc.id;

  const mainCn = clsx(
    'content-container',
    location.pathname.startsWith('/platform/mogie') && 'overflow-hidden',
  );

  const isWrongNetwork = isConnected && chain?.unsupported;

  if (authenticated) {
    return (
      <div className="app-container">
        <ThemeProvider theme={theme}>
          <Switch>
            <Route path="/register-two-factor-auth">
              <TwoFactorAuth />
            </Route>
            <Route>
              <div className="sidebar-container">
                <Sidebar />
              </div>
            </Route>
          </Switch>
          <div id="main" className={mainCn}>
            {isBsc && showBanner && (
              <Paper
                className={`h-auto py-4 px-3 flex items-center justify-center gap-10 mt-16 sm:mt-0  ${classes.root} bsc-banner`}
              >
                <div className={`flex justify-center font-bold`}>
                  {NOTIFICATION_MSG}
                </div>
                <div>
                  <IconButton
                    className={classes.exitButton}
                    onClick={inValidateBanner}
                  >
                    <ClearIcon className={classes.exitIcon} />
                  </IconButton>
                </div>
              </Paper>
            )}
            <Switch>
              <Redirect exact from="/login" to="/platform/dashboard" />
              <Route
                path="/platform/dashboard"
                exact
                component={isWrongNetwork ? WrongNetworkPage : DashboardPage}
              />
              <Route
                path="/platform/auction"
                exact
                component={isWrongNetwork ? WrongNetworkPage : AuctionPage}
              />
              <Route
                path="/platform/nfts"
                exact
                component={isWrongNetwork ? WrongNetworkPage : NFTPage}
              />
              <Route
                path="/platform/farming"
                exact
                component={isWrongNetwork ? WrongNetworkPage : FarmingPage}
              />
              {/* MARKETPLACE */}
              <Route path="/platform/moonpay" exact component={MoonPay} />
              <Route
                path="/platform/marketplace"
                exact
                component={isWrongNetwork ? WrongNetworkPage : MarketplacePage}
              />

              <Route
                path="/platform/marketplace/owned"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : OwnedNftMarketplacePage
                }
              />

              <Route
                path="/platform/marketplace/owned?from=sidebar"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : OwnedNftMarketplacePage
                }
              />

              <Route
                path="/platform/nftDetails/:nftContractAddress/:nftId"
                exact
                component={isWrongNetwork ? WrongNetworkPage : NftDetailsPage}
              />
              <Route
                path="/platform/nftDetails/:chain/:nftContractAddress/:nftId"
                exact
                component={isWrongNetwork ? WrongNetworkPage : NftDetailsPage}
              />
              <Route
                path="/:nftContractAddress(0x[a-fA-F0-9]{40})/:nftId"
                exact
                component={isWrongNetwork ? WrongNetworkPage : NftDetailsPage}
              />
              <Route
                path="/platform/collection/:nftContractAddress"
                exact
                component={isWrongNetwork ? WrongNetworkPage : CollectionPage}
              />
              <Route
                path="/platform/collection/:chain/:nftContractAddress"
                exact
                component={isWrongNetwork ? WrongNetworkPage : CollectionPage}
              />

              <Route
                path="/platform/community-dashboard"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : CommunityDashboardPage
                }
              />
              <Route
                path="/platform/voting"
                exact
                component={isWrongNetwork ? WrongNetworkPage : VotingPage}
              />
              <Route
                path="/platform/voting/:roundId"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : PreviousRoundPage
                }
              />
              <Route
                path="/platform/voting/:roundId/:placeInPastRounds"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : PreviousRoundPage
                }
              />
              <Route
                path="/platform/voting/:roundId/nft/:movieId"
                exact
                component={isWrongNetwork ? WrongNetworkPage : ProjectPage}
              />
              <Route
                path="/platform/financedProject/:projectId"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : FinancedProjectPage
                }
              />
              <Route
                path="/platform/events"
                exact
                component={isWrongNetwork ? WrongNetworkPage : EventsPage}
              />
              <Route
                path="/platform/updates"
                exact
                component={isWrongNetwork ? WrongNetworkPage : UpdatesPage}
              />
              <Route
                path="/platform/live"
                exact
                component={isWrongNetwork ? WrongNetworkPage : MogulLivePage}
              />
              <Route
                path="/platform/profile"
                exact
                component={isWrongNetwork ? WrongNetworkPage : ProfilePage}
              />
              <Route
                path="/platform/projectSubmission"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : ProjectSubmissionPage
                }
              />
              <Route path="/platform/mogie" exact component={Mogie} />
              <Route path="/platform/mogie/:path" exact component={Mogie} />
              <Route
                path="/platform/creators/dashboard"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : CreatorsDashboard
                }
              />
              <Route
                path="/platform/creators/mint/:nftContractAddress/:nftId"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : NftMintingContainer
                }
              />
              <Route
                path="/platform/showcase"
                exact
                component={
                  isWrongNetwork ? WrongNetworkPage : LaunchPadListPage
                }
              />
              <Route
                path="/platform/showcase/:collectionSlug"
                exact
                component={isWrongNetwork ? WrongNetworkPage : LaunchPad}
              />
              <Route
                path="/platform/starsStaking"
                exact
                component={isWrongNetwork ? WrongNetworkPage : StarsStakingPage}
              />
              <Redirect to="/platform/dashboard" />
            </Switch>
          </div>
        </ThemeProvider>
      </div>
    );
  }

  return (
    <div className="app-container">
      <ThemeProvider theme={theme}>
        <Route
          path={[
            '/platform/dashboard',
            '/platform/moonpay',
            '/platform/auction',
            '/platform/farming',
            '/platform/nfts',
            '/platform/voting',
            '/platform/voting/:roundId',
            '/platform/voting/:roundId/:placeInPastRounds',
            '/platform/voting/:roundId/nft/:movieId',
            '/platform/marketplace',
            '/platform/marketplace/owned',
            '/platform/projectSubmission',
            '/platform/mogie',
            '/platform/mogie/:path',
            '/platform/community-dashboard',
            '/platform/collection/:nftContractAddress',
            '/platform/collection/:chain/:nftContractAddress',
            '/platform/nftDetails/:nftContractAddress/:nftId',
            '/platform/nftDetails/:chain/:nftContractAddress/:nftId',
            '/:nftContractAddress(0x[a-fA-F0-9]{40})/:nftId',
            '/platform/showcase/:collectionSlug',
            '/platform/showcase',
            '/platform/starsStaking',
          ]}
          exact
        >
          <div className="sidebar-container">
            <Sidebar />
          </div>
        </Route>
        <div id="main" className={mainCn}>
          {isBsc && showBanner && (
            <Paper
              className={`h-auto py-4 px-3 flex items-center justify-center gap-10 mt-16 sm:mt-0  ${classes.root} bsc-banner`}
            >
              <div className={`flex justify-center font-bold`}>
                {NOTIFICATION_MSG}
              </div>
              <div>
                <IconButton
                  className={classes.exitButton}
                  onClick={inValidateBanner}
                >
                  <ClearIcon className={classes.exitIcon} />
                </IconButton>
              </div>
            </Paper>
          )}
          <Switch>
            <Route exact path="/register">
              {state === AuthState.verificationRequired ? (
                <Redirect to="/login" />
              ) : (
                <RegisterPage />
              )}
            </Route>
            <Route path="/login" exact>
              <Login />
            </Route>
            <Route
              path="/:nftContractAddress(0x[a-fA-F0-9]{40})/:nftId"
              exact
              component={isWrongNetwork ? WrongNetworkPage : NftDetailsPage}
            />
            <Route
              path="/platform/dashboard"
              exact
              component={isWrongNetwork ? WrongNetworkPage : DashboardPage}
            />
            <Route
              path="/platform/moonpay"
              exact
              component={isWrongNetwork ? WrongNetworkPage : MoonPay}
            />

            <Route
              path="/platform/auction"
              exact
              component={isWrongNetwork ? WrongNetworkPage : AuctionPage}
            />
            <Route
              path="/reset-password"
              exact
              component={isWrongNetwork ? WrongNetworkPage : ResetPasswordPage}
            />
            <Route
              path="/platform/nfts"
              exact
              component={isWrongNetwork ? WrongNetworkPage : NFTPage}
            />
            <Route
              path="/platform/farming"
              exact
              component={isWrongNetwork ? WrongNetworkPage : FarmingPage}
            />
            <Route
              path="/platform/voting"
              exact
              component={isWrongNetwork ? WrongNetworkPage : VotingPage}
            />
            <Route
              path="/platform/voting/:roundId"
              exact
              component={isWrongNetwork ? WrongNetworkPage : PreviousRoundPage}
            />
            <Route
              path="/platform/voting/:roundId/:placeInPastRounds"
              exact
              component={isWrongNetwork ? WrongNetworkPage : PreviousRoundPage}
            />
            <Route
              path="/platform/voting/:roundId/nft/:movieId"
              exact
              component={isWrongNetwork ? WrongNetworkPage : ProjectPage}
            />
            <Route
              path="/platform/marketplace"
              exact
              component={isWrongNetwork ? WrongNetworkPage : MarketplacePage}
            />
            <Route
              path="/platform/collection/:nftContractAddress"
              exact
              component={isWrongNetwork ? WrongNetworkPage : CollectionPage}
            />
            <Route
              path="/platform/collection/:chain/:nftContractAddress"
              exact
              component={isWrongNetwork ? WrongNetworkPage : CollectionPage}
            />
            <Route
              path="/platform/marketplace/owned"
              exact
              component={
                isWrongNetwork ? WrongNetworkPage : OwnedNftMarketplacePage
              }
            />
            <Route
              path="/platform/nftDetails/:nftContractAddress/:nftId"
              exact
              component={isWrongNetwork ? WrongNetworkPage : NftDetailsPage}
            />
            <Route
              path="/platform/nftDetails/:chain/:nftContractAddress/:nftId"
              exact
              component={isWrongNetwork ? WrongNetworkPage : NftDetailsPage}
            />
            <Route
              path="/platform/projectSubmission"
              exact
              component={
                isWrongNetwork ? WrongNetworkPage : ProjectSubmissionPage
              }
            />
            <Route
              path="/platform/mogie"
              exact
              component={isWrongNetwork ? WrongNetworkPage : Mogie}
            />
            <Route
              path="/platform/mogie/:path"
              exact
              component={isWrongNetwork ? WrongNetworkPage : Mogie}
            />
            <Route
              path="/platform/showcase"
              exact
              component={isWrongNetwork ? WrongNetworkPage : LaunchPadListPage}
            />
            <Route
              path="/platform/showcase/:collectionSlug"
              exact
              component={isWrongNetwork ? WrongNetworkPage : LaunchPad}
            />
            <Route
              path="/platform/starsStaking"
              exact
              component={isWrongNetwork ? WrongNetworkPage : StarsStakingPage}
            />
            <Redirect to="/platform/dashboard" />
          </Switch>
        </div>
      </ThemeProvider>
    </div>
  );
};
